import styled from '@emotion/styled/macro'
import * as React from 'react'
import { forwardRef, useLayoutEffect, useRef } from 'react'
import type { CSSProperties } from 'react'

import type { Color } from '~/types/Color'
import type { Customization } from '~/types/Customization'
import type { Design, View } from '~/types/Design'

import highlightImage from '~/assets/images/parts-highlight.png'

import { PrintLogo } from './PrintLogo'
import { PrintName } from './PrintName'

export interface PreviewRendererProps {
  className?: string
  style?: CSSProperties

  colors: readonly Color[]
  design: Design

  view: View

  customization: Customization

  currentScene?:
    | { type: 'part'; part: { id: string } }
    | { type: 'print_name' }
    | { type: 'print_logo' }

  printNamePlaceholder?: string

  imageHeight?: CSSProperties['height']

  onLoadBaseImage?(image: HTMLImageElement): void

  /**
   * パーツハイライトアニメーションを強制的にトリガーするためのシグナル
   * この値が変更されるたびに、選択中のパーツのアニメーションが再開される
   */
  triggerHighlightSignal?: number
}

// eslint-disable-next-line react/display-name
export const PreviewRenderer = forwardRef<HTMLDivElement, PreviewRendererProps>(
  (
    {
      colors,
      design,
      view,
      customization,
      currentScene,
      printNamePlaceholder,
      imageHeight,
      onLoadBaseImage,
      triggerHighlightSignal,
      ...rest
    },
    ref
  ) => {
    return (
      <Container {...rest} ref={ref}>
        <Canvas
          role="img"
          aria-label={`シミュレーションのプレビュー、${view.description}`}
        >
          <BaseImage
            style={{ height: imageHeight || 'auto' }}
            src={view.baseImage}
            onLoad={(ev) => {
              onLoadBaseImage?.(ev.currentTarget)
            }}
          />
          {view.areas.map((area) => {
            const partCustomization = customization.parts[area.id]

            const isSelected =
              currentScene?.type === 'part' && currentScene.part.id === area.id

            return (
              <AreaLayer
                key={area.id}
                colors={colors}
                selected={isSelected}
                area={area}
                customization={partCustomization || null}
                triggerHighlightSignal={triggerHighlightSignal}
              />
            )
          })}
          {design.printName &&
            design.printName.id === view.id &&
            !!customization.printName &&
            !!customization.printName.printType && (
              <PrintName
                design={design.printName}
                customization={customization.printName}
                placeholder={printNamePlaceholder}
              />
            )}
          {design.printLogo &&
            design.printLogo.id === view.id &&
            customization.printLogo &&
            customization.printLogo.printType !== null && (
              <PrintLogo
                design={design.printLogo}
                customization={customization.printLogo}
              />
            )}
        </Canvas>
      </Container>
    )
  }
)

interface AreaLayerProps {
  area: View['areas'][number]

  colors: readonly Color[]
  customization: Customization['parts'][number]

  selected: boolean

  triggerHighlightSignal?: number
}

const AreaLayer = ({
  selected,
  customization,
  colors,
  area,
  triggerHighlightSignal
}: AreaLayerProps) => {
  const animationTarget = useRef<HTMLDivElement>(null)
  const animationRef = useRef<Animation | null>(null)
  const prevSelectedRef = useRef(selected)

  // CSSのアニメーションをそのまま使うと、未選択のアニメーションの後に色を選択すると、
  // 色選択済みでシーンを表示した際のアニメーションがキックされてしまう。
  // これを防ぐために選択時に Web Animation API でアニメーションを再生している
  useLayoutEffect(() => {
    // アニメーションを開始する条件：
    // 1. 現在選択されている
    // 2. かつ、以下のいずれか：
    //    a) 前回は未選択だった（通常のケース）
    //    b) triggerHighlightSignalが変更された（ビュー切り替え後のケース）
    const shouldAnimate =
      selected &&
      (!prevSelectedRef.current || triggerHighlightSignal !== undefined)

    prevSelectedRef.current = selected

    if (!shouldAnimate || !animationTarget.current) {
      return
    }

    // 既存のアニメーションをキャンセル
    if (animationRef.current) {
      animationRef.current.cancel()
    }

    const anim = animationTarget.current.animate(
      // 色を選択しているかによって底の値が異なるが、ここで判定して値を設定すると
      // アニメーション中に色が選択された際に未選択の値を使い続けてしまうため、
      // CSS変数にして途中で切り替えた場合は選択済みの底の値を使うようにしている。
      [{ opacity: 1 }, { opacity: 'var(--_opacity-min)' }, { opacity: 1 }],
      {
        easing: 'linear',
        duration: 1000,
        iterations: Number.POSITIVE_INFINITY,
        direction: customization ? 'alternate' : 'normal'
      }
    )

    animationRef.current = anim

    return () => {
      anim.cancel()
      animationRef.current = null
    }
  }, [selected, customization, triggerHighlightSignal])

  // 色が変更されたら点滅を停止
  useLayoutEffect(() => {
    if (customization && animationRef.current) {
      animationRef.current.cancel()
      animationRef.current = null
    }
  }, [customization])

  const commonStyle: CSSProperties = {
    WebkitMaskImage: `url(${area.maskImage})`,
    maskImage: `url(${area.maskImage})`
  }

  const color = colors.find((c) => c.id === customization?.color.id)

  const blendMode = (color?.cssBlendMode ??
    'multiply') as CSSProperties['mixBlendMode']

  return (
    <ColorLayer
      ref={animationTarget}
      style={
        color
          ? {
              ...commonStyle,
              backgroundColor: color.colorCode,
              filter: `brightness(${color.brightness * 100}%)`,
              mixBlendMode: blendMode,
              opacity: color.opacity
            }
          : commonStyle
      }
      data-empty={(selected && !customization) || undefined}
    />
  )
}

const Container = styled.div`
  display: inline-flex;
  overflow: visible;
`

const Canvas = styled.div`
  position: relative;
  text-align: center;
  aspect-ratio: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const BaseImage = styled.img`
  object-fit: contain;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.15))
    drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.08));
`

const ColorLayer = styled.div`
  --_opacity-min: 0.5;

  position: absolute;
  top: 0;
  left: 50%;
  aspect-ratio: 1 / 1;
  width: 100%;
  height: 100%;
  mask-position: center center;
  inline-size: max-content;

  mask-size: contain;
  mask-repeat: no-repeat;
  object-fit: contain;

  transition: background-color 0.3s linear, filter 0.3s linear;
  will-change: background-color, filter;
  // フィルタがコロコロ変わるため、明示的にGPUパワーを引き出す
  transform: translate3D(0, 0, 0) translateX(-50%);

  &[data-empty] {
    --_opacity-min: 0;

    background-image: url(${highlightImage});
    background-size: cover;
  }
`
